import React, {
  SubmitEventHandler,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  StLocationSearchHero,
  StHeroBackgroundImage,
  StHeroContent,
  StHeroBadge,
  StHeroHeadlineWrap,
  StHeroSubtitle,
  StSearchBar,
  StSearchBarMainRow,
  StSearchInputRow,
  StMapPinWrap,
  StLocationInput,
  PrimaryCtaOutline,
  StSearchActions,
  StToggleRow,
  StToggleTrack,
  StToggleLabel,
  StValidationMessage,
  StCommunitiesRow,
  StCommunitiesLabel,
  StCommunityPills,
  StCommunityPill,
  StSearchBarWrapper,
  StSuggestionsList,
  StSuggestionItem,
} from './LocationSearchHero.styles';
import { useTheme } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import { RichText, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useDispatch, useSelector } from 'react-redux';
import UiHeading from 'components/shared/Heading';
import UiText from 'components/shared/Typography/UiText';
import ButtonPrimary from 'components/shared/Button/PrimaryButton';
import CustomNextImage from 'components/shared/CustomNextImage/CustomNextImage';
import { SearchLocationHeroProps } from '../LocationSearch.types';
import { StNextImage } from 'components/shared/NextImage.styles';
import { RootState } from 'src/store/store';
import InputAdornment from '@mui/material/InputAdornment';
import { setGeolocation } from '../../../features/geolocationSlice';
import { hideLoader } from '../../../features/loaderSlice';
import { clearLocationContext } from '../../../features/locationSearchSlice';
import {
  geocodeLocation,
  isValidLocationQuery,
  normalizeUsZipCode,
  type GeocodedLocation,
} from 'lib/utils/coveo/sitesHeadless/geocodeLocation';
import {
  fetchLocationSuggestions,
  LocationSuggestion,
  resolvePlaceCoords,
} from 'lib/utils/coveo/sitesHeadless/fetchLocationSuggestions';
import { loadGoogleMapsMapLibrary } from 'lib/utils/googleMaps/loadGoogleMapsMapLibrary';

const SUGGESTION_DEBOUNCE_MS = 300;
const MIN_SUGGESTION_INPUT_LENGTH = 2;

type ResolvedCoords = GeocodedLocation;

type ResolvedLocation = {
  display: string;
  city: string | null;
  state: string | null;
  stateCode: string | null;
  zipcode: string | null;
};

type ReduxLocation = {
  latitude?: number | null;
  longitude?: number | null;
  zipcode?: string | null;
  city?: string | null;
  state?: string | null;
  stateCode?: string | null;
};

type SiteSettingsAddressTypeItem = {
  fields?: {
    name?: {
      value?: string;
    };
  };
};

type LocationSearchSiteSettings = {
  googleMapsAPIKey?: { value?: string };
  addressType?: SiteSettingsAddressTypeItem[];
  cityType?: SiteSettingsAddressTypeItem[];
  stateType?: SiteSettingsAddressTypeItem[];
  zipCodeType?: { value?: string };
  countryCode?: { value?: string };
};

type ReverseGeocodeAddressTypeConfig = {
  cityTypes?: string[];
  stateTypes?: string[];
  zipCodeType?: string;
};

const stripRichText = (value?: string): string => (value ?? '').replace(/<[^>]*>/g, '').trim();

const resolveBadgeText = (value: string | undefined, count: number): string =>
  (value ?? '').replace('{count}', String(count));

const formatBrowseByLabel = (value?: string): string => {
  const label = value?.trim() || 'Browse by communities';
  return label.endsWith(':') ? label : `${label}:`;
};

const isGoogleMapsReady = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.google?.maps?.Geocoder);

const DEFAULT_CITY_TYPES = ['locality', 'postal_town'];
const DEFAULT_STATE_TYPES = ['administrative_area_level_1'];
const DEFAULT_ZIP_CODE_TYPE = 'postal_code';

const getConfiguredAddressTypes = (types?: SiteSettingsAddressTypeItem[]): string[] =>
  types
    ?.map((item) => item?.fields?.name?.value?.trim())
    .filter((value): value is string => Boolean(value)) ?? [];

const getGeocodeOptionsFromSiteSettings = (siteSettings?: LocationSearchSiteSettings) => {
  const cityTypes = getConfiguredAddressTypes(siteSettings?.cityType);
  const stateTypes = getConfiguredAddressTypes(siteSettings?.stateType);
  const zipCodeType = siteSettings?.zipCodeType?.value?.trim() || undefined;
  const acceptedTypes = Array.from(
    new Set([
      ...getConfiguredAddressTypes(siteSettings?.addressType),
      ...cityTypes,
      ...stateTypes,
      ...(zipCodeType ? [zipCodeType] : []),
    ])
  );
  const countryCode = siteSettings?.countryCode?.value?.trim() || undefined;

  return {
    ...(acceptedTypes.length > 0 ? { acceptedTypes } : {}),
    ...(cityTypes.length > 0 ? { cityTypes } : {}),
    ...(stateTypes.length > 0 ? { stateTypes } : {}),
    ...(zipCodeType ? { zipCodeType } : {}),
    ...(countryCode ? { countryCode } : {}),
  };
};

const getMapApiKey = (siteSettings?: LocationSearchSiteSettings): string | undefined => {
  try {
    return siteSettings?.googleMapsAPIKey?.value
      ? atob(siteSettings.googleMapsAPIKey.value)
      : undefined;
  } catch {
    return undefined;
  }
};

const parseAddressComponents = (
  addressComponents: google.maps.GeocoderAddressComponent[],
  addressTypeConfig?: ReverseGeocodeAddressTypeConfig
): ResolvedLocation | null => {
  const findComponentByTypeOrder = (types: string[]) => {
    for (const type of types) {
      const component = addressComponents.find((item) => item.types.includes(type));
      if (component) {
        return component;
      }
    }
    return undefined;
  };

  const cityComponent = findComponentByTypeOrder(
    addressTypeConfig?.cityTypes && addressTypeConfig.cityTypes.length > 0
      ? addressTypeConfig.cityTypes
      : DEFAULT_CITY_TYPES
  );
  const stateComponent = findComponentByTypeOrder(
    addressTypeConfig?.stateTypes && addressTypeConfig.stateTypes.length > 0
      ? addressTypeConfig.stateTypes
      : DEFAULT_STATE_TYPES
  );
  const zipCodeType = addressTypeConfig?.zipCodeType || DEFAULT_ZIP_CODE_TYPE;
  const zipComponent = addressComponents.find((component) => component.types.includes(zipCodeType));

  const city = cityComponent?.long_name ?? null;
  const state = stateComponent?.long_name ?? null;
  const stateCode = stateComponent?.short_name ?? null;
  const zipcode = normalizeUsZipCode(zipComponent?.long_name ?? null);

  const locality = [city, state].filter(Boolean).join(', ');
  const display = [locality, zipcode].filter(Boolean).join(' - ');

  if (!display) {
    return null;
  }

  return { display, city, state, stateCode, zipcode };
};

/**
 * "City, ST" for the site detail breadcrumb, built from what Google returns for
 * the searched location. Falls back to the raw query when the geocoder gives no
 * usable components.
 */
const formatLocality = (
  city?: string | null,
  stateCode?: string | null,
  fallback?: string
): string => {
  const locality = [city, stateCode].filter(Boolean).join(', ');
  return locality || (fallback ?? '').replace(/,\s*USA$/i, '').trim();
};

const formatDisplayFromReduxLocation = (location: ReduxLocation): string => {
  const locality = [location.city, location.state].filter(Boolean).join(', ');
  return [locality, location.zipcode].filter(Boolean).join(' - ');
};

const hasUsableReduxLocation = (
  location: ReduxLocation | null | undefined
): location is ReduxLocation => {
  if (!location) {
    return false;
  }

  const hasDisplayParts = Boolean(location.city || location.state || location.zipcode);
  const hasCoords = location.latitude != null && location.longitude != null;
  return hasDisplayParts || hasCoords;
};

const reverseGeocodeCoords = (
  latitude: number,
  longitude: number,
  addressTypeConfig?: ReverseGeocodeAddressTypeConfig
): Promise<ResolvedLocation> =>
  new Promise((resolve, reject) => {
    if (!isGoogleMapsReady()) {
      reject(new Error('Google Maps is not ready'));
      return;
    }

    const geocoder = new google.maps.Geocoder();
    const latLng = new google.maps.LatLng(latitude, longitude);

    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const resolved = parseAddressComponents(results[0].address_components, addressTypeConfig);
        if (resolved) {
          resolve(resolved);
          return;
        }
      }
      reject(new Error('Geocoding request failed'));
    });
  });

const getGeolocationErrorMessage = (
  error: GeolocationPositionError,
  messages: {
    permissionDenied?: string;
    positionUnavailable?: string;
    validationErrorText?: string;
  }
): string => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return messages.permissionDenied || 'Kindly enable location access';
    case error.POSITION_UNAVAILABLE:
      return messages.positionUnavailable || 'Location services unavailable';
    default:
      return messages.validationErrorText || 'Unable to get your location';
  }
};

const GEO_POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: 60000,
};

const SearchLocationHero = (props: SearchLocationHeroProps): JSX.Element => {
  const [useMyLocation, setUseMyLocation] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isLocationAccessError, setIsLocationAccessError] = useState(false);
  const [pendingCoords, setPendingCoords] = useState<ResolvedCoords | null>(null);
  const [resolvedCoords, setResolvedCoords] = useState<ResolvedCoords | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isResolvingSuggestion, setIsResolvingSuggestion] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const validationErrorId = useId();
  const listboxId = useId();
  const toggleSwitchId = useId();
  const toggleLabelId = useId();
  const blurCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the query a programmatic seed wrote, so the guard is consumed by the
  // effect run for that query instead of the one before it lands.
  const suppressSuggestionsForQueryRef = useRef<string | null>(null);
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    fields,
    nationwideTotal = 0,
    communities = [],
    selectedCommunities = [],
    onCommunitySelect,
    onLocationSearch,
    profileLocationQuery,
    profileGeocodeQuery,
    profileCoords,
  } = props;
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings = sitecoreContext?.siteSettings as LocationSearchSiteSettings;
  const addressTypeConfig = useMemo<ReverseGeocodeAddressTypeConfig>(
    () => ({
      cityTypes: getConfiguredAddressTypes(siteSettings?.cityType),
      stateTypes: getConfiguredAddressTypes(siteSettings?.stateType),
      zipCodeType: siteSettings?.zipCodeType?.value?.trim() || DEFAULT_ZIP_CODE_TYPE,
    }),
    [siteSettings?.cityType, siteSettings?.stateType, siteSettings?.zipCodeType?.value]
  );
  const googleMapsApiKeyValue = siteSettings?.googleMapsAPIKey?.value;
  const reduxLocation = useSelector(
    (state: RootState) => state.geolocation?.location as ReduxLocation | null | undefined
  );
  const previousSearchCoords = useSelector(
    (state: RootState) => state.locationSearch?.coords ?? null
  );
  const previousSearchLabel = useSelector(
    (state: RootState) => state.locationSearch?.locationLabel ?? null
  );
  const previousSearchZipCode = useSelector(
    (state: RootState) => state.locationSearch?.searchZipCode ?? null
  );
  const hasPreviousSearch = useSelector((state: RootState) =>
    Boolean(state.locationSearch?.hasSearched)
  );
  const permissionDeniedMessage = fields?.permissionDenied?.value;
  const positionUnavailableMessage = fields?.positionUnavailable?.value;
  const validationErrorTextMessage = fields?.validationErrorText?.value;

  useEffect(() => {
    dispatch(hideLoader());
  }, [dispatch]);

  const geoErrorMessages = {
    permissionDenied: permissionDeniedMessage,
    positionUnavailable: positionUnavailableMessage,
    validationErrorText: validationErrorTextMessage,
  };

  const requestUserLocation = useCallback(
    (options: { showError: boolean; isCancelled?: () => boolean }): (() => void) | void => {
      if (!navigator.geolocation) {
        if (options.showError) {
          setIsLocationAccessError(true);
          setValidationError('Geolocation is not available in this browser');
        }
        return;
      }

      const applySuccess = (position: GeolocationPosition) => {
        if (options.isCancelled?.()) {
          return;
        }
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setIsLocationAccessError(false);
        setValidationError('');
        setUseMyLocation(true);
        setResolvedCoords(coords);
        setPendingCoords(coords);
      };

      const applyFailure = (error: GeolocationPositionError) => {
        if (options.isCancelled?.()) {
          return;
        }
        setPendingCoords(null);
        setResolvedCoords(null);
        setUseMyLocation(false);
        if (options.showError) {
          setIsLocationAccessError(true);
          setValidationError(getGeolocationErrorMessage(error, geoErrorMessages));
        }
      };

      const fetchPosition = () => {
        navigator.geolocation.getCurrentPosition(applySuccess, applyFailure, GEO_POSITION_OPTIONS);
      };

      // Explicit toggle: turn on immediately, fill location when coords resolve
      if (options.showError) {
        setIsLocationAccessError(false);
        setValidationError('');
        setUseMyLocation(true);
        fetchPosition();
        return;
      }

      // Mount auto-request: enable toggle as soon as access is known/granted
      let permissionStatus: PermissionStatus | null = null;

      const handlePermissionChange = () => {
        if (options.isCancelled?.() || permissionStatus?.state !== 'granted') {
          return;
        }
        setUseMyLocation(true);
        fetchPosition();
      };

      const cleanupPermissionListener = () => {
        permissionStatus?.removeEventListener('change', handlePermissionChange);
      };

      if (!navigator.permissions?.query) {
        fetchPosition();
        return cleanupPermissionListener;
      }

      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((status) => {
          if (options.isCancelled?.()) {
            return;
          }

          permissionStatus = status;

          if (status.state === 'granted') {
            setUseMyLocation(true);
            fetchPosition();
            return;
          }

          if (status.state === 'denied') {
            // Listen so a later grant in browser settings can recover (with fetchPosition)
            status.addEventListener('change', handlePermissionChange);
            return;
          }

          // prompt: request position (shows browser dialog) and fetch on grant
          status.addEventListener('change', handlePermissionChange);
          fetchPosition();
        })
        .catch(() => {
          if (options.isCancelled?.()) {
            return;
          }
          fetchPosition();
        });

      return cleanupPermissionListener;
    },
    [permissionDeniedMessage, positionUnavailableMessage, validationErrorTextMessage]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setIsSuggestionsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  useEffect(() => {
    // Saved profile location. The city crumb does not pass this, so the previous search stays.
    // "Use my location" stays off: that toggle means device GPS.
    if (profileLocationQuery || profileGeocodeQuery || profileCoords) {
      if (profileLocationQuery) {
        suppressSuggestionsForQueryRef.current = profileLocationQuery;
        setLocationQuery(profileLocationQuery);
      }
      setUseMyLocation(false);

      if (profileCoords) {
        setResolvedCoords({ latitude: profileCoords.lat, longitude: profileCoords.lng });
        onLocationSearch?.(profileCoords, profileLocationQuery || undefined, {
          scrollToResults: false,
        });
        return;
      }

      const addressQuery = profileGeocodeQuery || profileLocationQuery;
      if (!addressQuery) {
        return;
      }
      let cancelled = false;
      const mapApiKey = getMapApiKey(siteSettings);
      const fallbackQuery = profileLocationQuery?.replace(' - ', ' ');
      // The box already shows "City, State - ZIP". Coordinates come from the full address.
      // If that address cannot be resolved, use the city, state, and ZIP.
      void geocodeLocation(addressQuery, mapApiKey)
        .catch(() => null)
        .then((geocoded) => {
          if (geocoded || !fallbackQuery || fallbackQuery === addressQuery) {
            return geocoded;
          }
          return geocodeLocation(fallbackQuery, mapApiKey).catch(() => null);
        })
        .then((geocoded) => {
          if (cancelled) {
            return;
          }
          if (!geocoded) {
            // Same message a typed search shows when Google cannot resolve it.
            setValidationError(geoErrorMessages.positionUnavailable ?? '');
            return;
          }
          setResolvedCoords(geocoded);
          onLocationSearch?.(
            { lat: geocoded.latitude, lng: geocoded.longitude },
            formatLocality(geocoded.city, geocoded.stateCode, fallbackQuery || addressQuery),
            { scrollToResults: false }
          );
        });
      return () => {
        cancelled = true;
      };
    }

    // Returning from a site page (SPA nav or browser back) with an active
    // search: restore the previous location UI instead of wiping the redux
    // search state and re-prompting for geolocation.
    if (hasPreviousSearch && previousSearchCoords) {
      if (previousSearchLabel) {
        suppressSuggestionsForQueryRef.current = previousSearchLabel;
        setLocationQuery(previousSearchLabel);
      }
      setResolvedCoords({
        latitude: previousSearchCoords.lat,
        longitude: previousSearchCoords.lng,
        zipcode: previousSearchZipCode,
      });
      return;
    }

    // No breadcrumb handoff and no previous search: drop leftover search coords,
    // then resume geo if any.
    dispatch(clearLocationContext());

    if (hasUsableReduxLocation(reduxLocation)) {
      const display = formatDisplayFromReduxLocation(reduxLocation);
      if (display) {
        setLocationQuery(display);
        setUseMyLocation(true);
        if (reduxLocation.latitude != null && reduxLocation.longitude != null) {
          const searchZipCode = normalizeUsZipCode(reduxLocation.zipcode);
          const coords = {
            latitude: reduxLocation.latitude,
            longitude: reduxLocation.longitude,
            zipcode: searchZipCode,
          };
          setResolvedCoords(coords);
          onLocationSearch?.(
            { lat: coords.latitude, lng: coords.longitude },
            formatLocality(reduxLocation.city, reduxLocation.stateCode ?? reduxLocation.state),
            { scrollToResults: false, searchZipCode }
          );
        }
        return;
      }

      if (reduxLocation.latitude != null && reduxLocation.longitude != null) {
        setUseMyLocation(true);
        setPendingCoords({
          latitude: reduxLocation.latitude,
          longitude: reduxLocation.longitude,
        });
        return;
      }
    }

    let cancelled = false;
    const cleanup = requestUserLocation({
      showError: false,
      isCancelled: () => cancelled,
    });
    return () => {
      cancelled = true;
      cleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed from the profile, otherwise from the previous search or device location
  }, [profileCoords, profileGeocodeQuery, profileLocationQuery]);

  useEffect(() => {
    if (!isLocationAccessError || useMyLocation) {
      return;
    }

    if (!navigator.geolocation || !navigator.permissions?.query) {
      return;
    }

    let cancelled = false;
    let permissionStatus: PermissionStatus | null = null;

    const handlePermissionChange = () => {
      if (cancelled || permissionStatus?.state !== 'granted') {
        return;
      }
      requestUserLocation({ showError: true });
    };

    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((status) => {
        if (cancelled) {
          return;
        }
        permissionStatus = status;
        status.addEventListener('change', handlePermissionChange);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      permissionStatus?.removeEventListener('change', handlePermissionChange);
    };
  }, [isLocationAccessError, useMyLocation, requestUserLocation]);

  useEffect(() => {
    if (!pendingCoords) {
      return;
    }

    let cancelled = false;

    const resolvePendingLocation = async () => {
      if (!isGoogleMapsReady()) {
        const mapApiKey = getMapApiKey(
          googleMapsApiKeyValue ? { googleMapsAPIKey: { value: googleMapsApiKeyValue } } : undefined
        );
        if (mapApiKey) {
          try {
            await loadGoogleMapsMapLibrary(mapApiKey);
          } catch {
            // Maps load failed; handled below
          }
        }
      }

      if (cancelled) {
        return;
      }

      if (!isGoogleMapsReady()) {
        setPendingCoords(null);
        setValidationError('Unable to resolve location');
        return;
      }

      try {
        const resolved = await reverseGeocodeCoords(
          pendingCoords.latitude,
          pendingCoords.longitude,
          addressTypeConfig
        );
        if (cancelled) {
          return;
        }
        setLocationQuery(resolved.display);
        setValidationError('');
        setPendingCoords(null);
        const searchZipCode = normalizeUsZipCode(resolved.zipcode);
        setResolvedCoords({
          latitude: pendingCoords.latitude,
          longitude: pendingCoords.longitude,
          city: resolved.city,
          stateCode: resolved.stateCode,
          zipcode: searchZipCode,
        });
        dispatch(
          setGeolocation({
            location: {
              latitude: pendingCoords.latitude,
              longitude: pendingCoords.longitude,
              zipcode: searchZipCode,
              city: resolved.city,
              state: resolved.state,
              stateCode: resolved.stateCode,
            },
            error: null,
          })
        );
        clearSuggestions();
        onLocationSearch?.(
          { lat: pendingCoords.latitude, lng: pendingCoords.longitude },
          formatLocality(resolved.city, resolved.stateCode, resolved.display),
          { scrollToResults: false, searchZipCode }
        );
      } catch {
        if (cancelled) {
          return;
        }
        setValidationError('Unable to resolve location');
        setPendingCoords(null);
      }
    };

    void resolvePendingLocation();

    return () => {
      cancelled = true;
    };
  }, [
    pendingCoords,
    googleMapsApiKeyValue,
    dispatch,
    clearSuggestions,
    onLocationSearch,
    addressTypeConfig,
  ]);

  useEffect(() => {
    if (suppressSuggestionsForQueryRef.current === locationQuery) {
      suppressSuggestionsForQueryRef.current = null;
      clearSuggestions();
      return;
    }

    if (useMyLocation || locationQuery.trim().length < MIN_SUGGESTION_INPUT_LENGTH) {
      clearSuggestions();
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      const mapApiKey = getMapApiKey(
        googleMapsApiKeyValue ? { googleMapsAPIKey: { value: googleMapsApiKeyValue } } : undefined
      );
      const nextSuggestions = await fetchLocationSuggestions(locationQuery, mapApiKey);
      if (cancelled) {
        return;
      }
      setSuggestions(nextSuggestions);
      setIsSuggestionsOpen(nextSuggestions.length > 0);
      setHighlightedIndex(-1);
    }, SUGGESTION_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [locationQuery, useMyLocation, googleMapsApiKeyValue, clearSuggestions]);

  useEffect(() => {
    return () => {
      if (blurCloseTimerRef.current) {
        clearTimeout(blurCloseTimerRef.current);
      }
    };
  }, []);

  if (!fields) {
    return <></>;
  }

  const locationIconSrc = fields.locationIcon?.value?.src;
  const searchPlaceholder = fields.searchPlaceholderText?.value || 'ZIP code, city or state...';
  const searchAriaLabel = stripRichText(searchPlaceholder) || 'Location search';
  const showValidationError = Boolean(validationError);
  const hasHeroImage = [fields.desktopImage, fields.tabletImage, fields.mobileImage].some(
    (field) => field?.value?.src
  );
  const showSuggestions = isInputFocused && isSuggestionsOpen && suggestions.length > 0;

  const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Typing implies focus; the dropdown stays closed for programmatic seeds only.
    setIsInputFocused(true);
    setLocationQuery(event.target.value);
    if (useMyLocation) {
      setUseMyLocation(false);
      setPendingCoords(null);
    }
    setResolvedCoords(null);
    if (validationError) {
      setValidationError('');
    }
    if (isLocationAccessError) {
      setIsLocationAccessError(false);
    }
  };

  const handleUseMyLocationToggle = () => {
    if (useMyLocation) {
      setUseMyLocation(false);
      setLocationQuery('');
      setPendingCoords(null);
      setResolvedCoords(null);
      return;
    }

    requestUserLocation({ showError: true });
  };

  const handleSelectSuggestion = async (suggestion: LocationSuggestion) => {
    suppressSuggestionsForQueryRef.current = suggestion.description;
    setLocationQuery(suggestion.description);
    clearSuggestions();
    setValidationError('');
    setIsResolvingSuggestion(true);

    try {
      const mapApiKey = getMapApiKey(siteSettings);
      const coords = await resolvePlaceCoords(suggestion.placeId, mapApiKey);
      if (coords) {
        setResolvedCoords(coords);
      } else {
        setResolvedCoords(null);
      }
    } finally {
      setIsResolvingSuggestion(false);
    }
  };

  const handleSearchClick = async () => {
    clearSuggestions();

    if (fields.isValidationRequired?.value && !locationQuery.trim()) {
      setValidationError(fields.validationErrorText?.value || 'Error');
      return;
    }

    setValidationError('');

    if (!onLocationSearch) {
      return;
    }

    if (!isValidLocationQuery(locationQuery)) {
      setValidationError(geoErrorMessages.positionUnavailable ?? '');
      return;
    }

    setIsSearching(true);
    try {
      if (resolvedCoords) {
        onLocationSearch(
          { lat: resolvedCoords.latitude, lng: resolvedCoords.longitude },
          formatLocality(resolvedCoords.city, resolvedCoords.stateCode, locationQuery),
          { searchZipCode: resolvedCoords.zipcode ?? null }
        );
        return;
      }

      const mapApiKey = getMapApiKey(siteSettings);
      const geocoded = await geocodeLocation(
        locationQuery,
        mapApiKey,
        getGeocodeOptionsFromSiteSettings(siteSettings)
      );
      if (!geocoded) {
        setValidationError(geoErrorMessages.positionUnavailable ?? '');
        return;
      }

      setResolvedCoords(geocoded);
      onLocationSearch(
        { lat: geocoded.latitude, lng: geocoded.longitude },
        formatLocality(geocoded.city, geocoded.stateCode, locationQuery),
        { searchZipCode: geocoded.zipcode ?? null }
      );
    } catch {
      setValidationError(geoErrorMessages.positionUnavailable ?? '');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (isSearching || isResolvingSuggestion) {
      return;
    }
    void handleSearchClick();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (showSuggestions && highlightedIndex >= 0) {
        event.preventDefault();
        void handleSelectSuggestion(suggestions[highlightedIndex]);
        return;
      }
      event.preventDefault();
      if (!isSearching && !isResolvingSuggestion) {
        void handleSearchClick();
      }
      return;
    }

    if (!showSuggestions) {
      if (event.key === 'Escape') {
        clearSuggestions();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      clearSuggestions();
    }
  };

  const handleInputBlur = () => {
    blurCloseTimerRef.current = setTimeout(() => {
      setIsInputFocused(false);
      clearSuggestions();
    }, 150);
  };

  const handleInputFocus = () => {
    if (blurCloseTimerRef.current) {
      clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
    setIsInputFocused(true);
    if (suggestions.length > 0 && !useMyLocation) {
      setIsSuggestionsOpen(true);
    }
  };

  return (
    <StLocationSearchHero id="location-search-hero">
      {hasHeroImage && (
        <StHeroBackgroundImage aria-hidden>
          <CustomNextImage
            mobileImg={fields.mobileImage}
            tabletImg={fields.tabletImage}
            desktopImg={fields.desktopImage}
            objectFit="cover"
            objectPosition="center"
            priority
          />
        </StHeroBackgroundImage>
      )}

      <StHeroContent id="hero-wrapper">
        {nationwideTotal > 0 && (
          <StHeroBadge id="location-search-badge">
            <RichText
              field={{ value: resolveBadgeText(fields.badgeText?.value, nationwideTotal) }}
            />
          </StHeroBadge>
        )}
        {fields.primaryHeading && (
          <StHeroHeadlineWrap>
            <UiHeading
              size="h1"
              isChildren
              styles={{
                fontSize: '48px',
                lineHeight: '53px',
                fontWeight: 700,
                color: (theme.palette as any)?.fontTitleColor1?.main,
              }}
              id="hero-heading"
            >
              <RichText field={fields.primaryHeading} tag="span" />
            </UiHeading>
          </StHeroHeadlineWrap>
        )}
        {fields.supportingSubtitle && (
          <StHeroSubtitle id="hero-description">
            <RichText field={fields.supportingSubtitle} tag="paragraph" />
          </StHeroSubtitle>
        )}
        <StSearchBarWrapper>
          <StSearchBar>
            <form onSubmit={handleSearchSubmit} noValidate>
              <StSearchBarMainRow>
                <StSearchInputRow>
                  {locationIconSrc && (
                    <StMapPinWrap>
                      <StNextImage field={fields.locationIcon} width="24" height="24" />
                    </StMapPinWrap>
                  )}
                  <StLocationInput
                    id="hero-input"
                    value={locationQuery}
                    onChange={handleLocationChange}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleInputBlur}
                    onFocus={handleInputFocus}
                    placeholder={searchPlaceholder}
                    variant="standard"
                    error={showValidationError}
                    inputProps={{
                      'aria-label': searchAriaLabel,
                      'aria-invalid': showValidationError,
                      'aria-autocomplete': 'list',
                      'aria-expanded': showSuggestions,
                      'aria-controls': showSuggestions ? listboxId : undefined,
                      ...(showSuggestions &&
                        highlightedIndex >= 0 && {
                          'aria-activedescendant': `location-suggestion-${highlightedIndex}`,
                        }),
                      role: 'combobox',
                      ...(showValidationError && { 'aria-describedby': validationErrorId }),
                    }}
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: ((!locationQuery && useMyLocation && !validationError) ||
                        isSearching ||
                        isResolvingSuggestion) && (
                        <InputAdornment position="end">
                          <CircularProgress size={24} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </StSearchInputRow>

                <StSearchActions>
                  {fields.useMyLocationText?.value && (
                    <StToggleRow id="hero-location-toggle">
                      <StToggleTrack
                        id={toggleSwitchId}
                        type="button"
                        role="switch"
                        aria-labelledby={toggleLabelId}
                        aria-checked={useMyLocation}
                        $checked={useMyLocation}
                        onClick={handleUseMyLocationToggle}
                      />
                      <StToggleLabel id={toggleLabelId} htmlFor={toggleSwitchId}>
                        <UiText
                          styles={{
                            fontSize: '14px',
                            lineHeight: '21px',
                            fontWeight: 500,
                            color: '#005F54',
                          }}
                        >
                          <Text tag="span" field={fields.useMyLocationText} />
                        </UiText>
                      </StToggleLabel>
                    </StToggleRow>
                  )}
                  {fields.searchSitesButtonText?.value && (
                    <PrimaryCtaOutline>
                      <ButtonPrimary
                        type="submit"
                        id="hero-search-button"
                        aria-label={fields.searchSitesButtonText?.value}
                        disabled={isSearching || isResolvingSuggestion}
                      >
                        {fields.searchSitesButtonText?.value}
                      </ButtonPrimary>
                    </PrimaryCtaOutline>
                  )}
                </StSearchActions>
              </StSearchBarMainRow>
            </form>
          </StSearchBar>
          {showValidationError && (
            <StValidationMessage id={validationErrorId} role="alert">
              <UiText
                styles={{
                  fontSize: '14px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: '#FFFFFF',
                }}
              >
                {validationError}
              </UiText>
            </StValidationMessage>
          )}
          {showSuggestions && (
            <StSuggestionsList id={listboxId} role="listbox" aria-label="Location suggestions">
              {suggestions.map((suggestion, index) => (
                <StSuggestionItem
                  key={suggestion.placeId}
                  id={`location-suggestion-${index}`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  $highlighted={index === highlightedIndex}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    void handleSelectSuggestion(suggestion);
                  }}
                >
                  {suggestion.description}
                </StSuggestionItem>
              ))}
            </StSuggestionsList>
          )}
        </StSearchBarWrapper>
        {communities.length > 0 && (
          <StCommunitiesRow id="hero-community-pills">
            <StCommunitiesLabel>
              <UiText
                styles={{
                  fontSize: '14px',
                  lineHeight: '20px',
                  fontWeight: 500,
                  color: '#D3E3DF',
                }}
              >
                {formatBrowseByLabel(fields.browseByLabel?.value)}
              </UiText>
            </StCommunitiesLabel>
            <StCommunityPills>
              {communities.map((item) => {
                const isSelected = selectedCommunities.includes(item);
                return (
                  <StCommunityPill
                    key={item}
                    type="button"
                    $selected={isSelected}
                    aria-pressed={isSelected}
                    onClick={() => onCommunitySelect?.(item)}
                  >
                    {item}
                  </StCommunityPill>
                );
              })}
            </StCommunityPills>
          </StCommunitiesRow>
        )}
      </StHeroContent>
    </StLocationSearchHero>
  );
};

export default SearchLocationHero;
